import { auth } from './firebase';
import { brand as brandConfig } from './branding';
import { confirmDialog } from './confirmStore';

interface SendNotificationParams {
  to: string;
  toName: string;
  subject: string;
  htmlBody: string;
  notificationType: string;
  applicationId: string;
}

interface SendPushParams {
  targetUid: string;
  title: string;
  body: string;
  url?: string;
}

/**
 * Send a system notification email to an applicant via the backend API.
 * Returns true on success, false on failure.
 */
export async function sendSystemNotification(params: SendNotificationParams): Promise<boolean> {
  try {
    const token = await auth.currentUser?.getIdToken();
    const res = await fetch('/api/email/send-system-notification', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(params),
    });
    const data = await res.json();
    return res.ok && data.success;
  } catch (err) {
    console.error('Failed to send system notification:', err);
    return false;
  }
}

/**
 * Send a push notification to an applicant via the backend API.
 * Uses the same FCM tokens the user registered on their device.
 * Returns true on success, false on failure.
 */
export async function sendPushNotification(params: SendPushParams): Promise<boolean> {
  const uid = params.targetUid;
  if (!uid) return false;

  try {
    const token = await auth.currentUser?.getIdToken();
    const res = await fetch('/api/push/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        targetUid: uid,
        title: params.title,
        body: params.body,
        url: params.url || (import.meta.env.VITE_FRONTEND_URL as string) || 'https://portal.isats.org',
      }),
    });
    const data = await res.json();
    return res.ok && data.success;
  } catch (err) {
    console.warn('[Push] Failed to send push notification:', err);
    return false;
  }
}

/**
 * Ask for admin confirmation before sending a notification.
 * @param message The confirmation message to show
 * @returns true if admin clicked Yes/OK, false otherwise
 *
 * ADM-01 close-out (Batch R2): this was the tree's last window.confirm
 * call site. It now routes through the sanctioned confirmDialog layer;
 * the contract is unchanged apart from becoming asynchronous — both
 * callers (ApplicantDetail program-package notify, useApplications
 * deactivation notice) already run in async handlers and now await it.
 */
export function confirmNotification(message: string): Promise<boolean> {
  return confirmDialog(message);
}

/**
 * Build a consistent HTML email template for system notifications.
 * Respects brand configuration from VITE_ env vars.
 */
export function buildEmailTemplate(options: {
  applicantName: string;
  title: string;
  bodyHtml: string;
  ctaUrl?: string;
  ctaText?: string;
  portalUrl?: string;
}): string {
  const bc = brandConfig;
  const frontendUrl = (window as any).__FRONTEND_URL || (import.meta.env.VITE_FRONTEND_URL as string) || 'https://portal.isats.org';
  const ctaUrl = options.ctaUrl || options.portalUrl || frontendUrl + '/applicant';
  const ctaText = options.ctaText || 'Log In to Your Portal';
  const portalUrl = options.portalUrl || frontendUrl + '/applicant';
  const now = new Date().getFullYear();
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<style>
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;margin:0;padding:0;background:#f0f2f5}
table{max-width:560px;margin:40px auto;background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.08)}
.logo{text-align:center;padding:36px 32px 8px 32px}
.logo span{font-size:28px;font-weight:800;color:${bc.colors.primaryDark};letter-spacing:2px}
.heading{text-align:center;padding:8px 32px 4px 32px}
.heading h1{color:${bc.colors.primaryDark};font-size:20px;font-weight:700;margin:0;padding:0 0 16px 0;border-bottom:2px solid #e8edf2}
.body{padding:24px 32px 32px 32px}
.body p{color:#334155;font-size:14px;line-height:1.7;margin:10px 0}
.body .data-table{width:100%;border-collapse:collapse;margin:18px 0;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden}
.body .data-table td{padding:10px 16px;font-size:13px;border-bottom:1px solid #e2e8f0;color:#475569}
.body .data-table td:first-child{font-weight:600;color:#1e293b;width:40%}
.body .data-table tr:last-child td{border-bottom:none}
.cta{text-align:center;margin:28px 0 8px 0}
.cta a{display:inline-block;background:${bc.colors.primaryDark};color:#ffffff;text-decoration:none;padding:12px 36px;border-radius:6px;font-weight:600;font-size:14px}
.cta a:hover{background:${bc.colors.primaryDark}}
.footer{text-align:center;padding:24px 32px;border-top:1px solid #e2e8f0}
.footer p{font-size:12px;color:#94a3b8;margin:4px 0;line-height:1.5}
.footer .sign-off{margin-top:12px;padding-top:12px;border-top:1px solid #e8edf2}
</style></head>
<body><table>
<tr><td class="logo"><span>${bc.businessName}</span></td></tr>
<tr><td class="heading"><h1>${options.title}</h1></td></tr>
<tr><td class="body">
<p>Dear ${options.applicantName},</p>
${options.bodyHtml}
<div class="cta"><a href="${ctaUrl}">${ctaText}</a></div>
<p style="font-size:12px;color:#94a3b8;text-align:center;margin-top:28px;">
  <a href="${portalUrl}" style="color:${bc.colors.primaryDark};">${portalUrl}</a>
</p>
</td></tr>
<tr><td class="footer">
<p>Regards,</p>
<p>The ${bc.businessName} Team</p>
<div class="sign-off">
<p>&copy; ${now} ${bc.businessName}. All rights reserved.</p>
</div>
</td></tr>
</table></body></html>`;
}
